import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Observable } from 'rxjs';
import { ColumnDefinition, ListItemClickEvent } from '../../../models/columnDefinition';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LargeShareholderSearchBoxComponent } from '../large-shareholder-search-box/large-shareholder-search-box.component';
import { LargeShareholderSearchFilter } from '../../../models/largeShareholderSearchFilter';
import { LargeShareholderService } from '../../../services/large-shareholder.service';
import { LargeShareholderDto } from '../../../models/api/largeShareholderDto';
import { PaginatedResponse } from '../../../models/paginatedResponse';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../store/states/app.state';
import { SetSearchFilter, SetPagination, SetSort } from '../../../store/actions/large-shareholder-list.actions';
import { selectLargeShareholderListFullState } from '../../../store/selectors/large-shareholder-list.selectors';
import { take, filter } from 'rxjs/operators';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-large-shareholder-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    GenericListComponent,
    NgxSpinnerModule,
    LargeShareholderSearchBoxComponent
  ],
  templateUrl: './large-shareholder-list.component.html',
  styleUrl: './large-shareholder-list.component.scss'
})
export class LargeShareholderListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<LargeShareholderDto>;

  largeShareholders: LargeShareholderDto[] = [];
  totalItems = 0;
  isSearchVisible = true;

  // Pagination and sorting state
  currentPage = 1;
  pageSize = 10;
  sortBy = 'name';
  ascending = true;
  currentSearchFilter: LargeShareholderSearchFilter = {};

  // Merge functionality state
  rightItem: LargeShareholderDto | undefined = undefined;
  matchedItems: LargeShareholderDto[] | undefined = undefined;

  showOwnerships = false;

  private allColumnDefinitions: ColumnDefinition[] = [
    //{ propertyName: 'year', displayName: 'Year' },
    { propertyName: 'code', displayName: 'Internal ID' },
    { propertyName: 'matchedYearsTooltip', displayName: '', dataType: 'tooltipIcon', sortable: false },
    { propertyName: 'name', displayName: 'Name', cellClassKey: 'nameCellClass', cellTooltipKey: 'nameCellTooltip' },
    { propertyName: 'ownershipDisplay', displayName: 'Ownerships', dataType: 'list', listClickable: true },
    //{ propertyName: 'address', displayName: 'Address' },
    //{ propertyName: 'website', displayName: 'Website' },
    { propertyName: 'countryName', displayName: 'Country' },
    { propertyName: 'categoryName', displayName: 'Category' },
    { propertyName: 'subCategoryName', displayName: 'Subcategory' },
  ];

  get columnDefinitions(): ColumnDefinition[] {
    if (this.showOwnerships) {
      return this.allColumnDefinitions;
    }
    return this.allColumnDefinitions.filter(c => c.propertyName !== 'ownershipDisplay');
  }

  constructor(
    private router: Router,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private largeShareholderService: LargeShareholderService,
    private store: Store<IAppState>,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    // Restore state from store
    this.store.select(selectLargeShareholderListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = state.searchFilter;
      this.currentPage = state.currentPage;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      // Load large shareholders with restored state
      this.loadLargeShareholders();
    });
  }

  ngAfterViewInit(): void {
    // Paginator synchronization is handled by GenericListComponent
  }

  private loadLargeShareholders(): void {
    this.spinnerService.show();

    this.largeShareholderService.searchLargeShareholders({
      ...this.currentSearchFilter,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (response) => {
        this.largeShareholders = response.data.map(ls => {
          const showMatchedYears = ls.matchedYears?.length && !ls.matchedYears.includes(ls.year);
          return {
            ...ls,
            matchedYearsTooltip: showMatchedYears ? `Matched in ${ls.matchedYears.join(', ')}` : undefined,
            nameCellClass: this.buildNameCellClass(ls),
            nameCellTooltip: this.buildNameCellTooltip(ls),
            ownershipDisplay: (ls.ownerships || [])
              .sort((a, b) => a.year - b.year || a.companyName.localeCompare(b.companyName))
              .map(o => ({
                label: `${o.companyName}${o.countryName ? ` [${o.countryName}]` : ''} (${o.year})${o.firstClassSharesPercentage != null ? ` - ${o.firstClassSharesPercentage.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : ''}`,
                companyId: o.companyId,
                year: o.year
              }))
          };
        });
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading large shareholders:', error);
        this.toast.error('Error loading large shareholders');
        this.spinnerService.hide();
      }
    });
  }

  /**
   * Marks the name of shareholders that take part in a pyramid and/or are connected to another
   * record, so both are visible from the list without opening the Pyramid and Connections tabs.
   * Styles live in styles.scss — the cell is rendered by generic-list, out of this component's
   * style scope.
   */
  private buildNameCellClass(largeShareholder: LargeShareholderDto): string {
    const classes: string[] = [];
    if (largeShareholder.isInPyramid) classes.push('shareholder-in-pyramid');
    if (largeShareholder.isConnected) classes.push('shareholder-connected');
    return classes.join(' ');
  }

  private buildNameCellTooltip(largeShareholder: LargeShareholderDto): string {
    const marks: string[] = [];
    if (largeShareholder.isInPyramid) marks.push('Takes part in a pyramid');
    if (largeShareholder.isConnected) marks.push('Connected shareholder');
    return marks.join(' • ');
  }

  async onListItemClick(event: ListItemClickEvent<LargeShareholderDto>): Promise<void> {
    if (event.column === 'ownershipDisplay' && event.listItem?.companyId) {
      try {
        await this.router.navigate(['/companies/edit', event.listItem.companyId], {
          queryParams: { tab: 'ownership', year: event.listItem.year }
        });
      } catch (error) {
        this.toast.error('Error navigating to Company edit (route not found)');
      }
    }
  }

  async onEditLargeShareholder(largeShareholder: LargeShareholderDto): Promise<void> {
    try {
      const year = this.currentSearchFilter.year
        ?? largeShareholder.latestYear
        ?? largeShareholder.year
        ?? Helpers.defaultMeasurementYear();
      await this.router.navigate(['/large-shareholders/edit', largeShareholder.id], {
        queryParams: { year: year }
      });
    } catch (error) {
      this.toast.error('Error navigating to Large Shareholder edit (route not found)');
    }
  }

  onOpenLargeShareholderInNewTab(largeShareholder: LargeShareholderDto): void {
    const year = this.currentSearchFilter.year
      ?? largeShareholder.latestYear
      ?? largeShareholder.year
      ?? Helpers.defaultMeasurementYear();
    const urlTree = this.router.createUrlTree(['/large-shareholders/edit', largeShareholder.id], {
      queryParams: { year: year }
    });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';

    // Update store
    this.store.dispatch(new SetSort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    // Reload data
    this.loadLargeShareholders();
  }

  onPageChange(event: PageEvent): void {
    // Update pagination state (API uses 1-based indexing)
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    // Update store
    this.store.dispatch(new SetPagination({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    }));

    // Reload data
    this.loadLargeShareholders();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.currentSearchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onLargeShareholderSearchFilter(largeShareholderSearchFilter: LargeShareholderSearchFilter): void {
    this.currentSearchFilter = largeShareholderSearchFilter;
    this.currentPage = 1; // Reset to first page on new search

    // Update store
    this.store.dispatch(new SetSearchFilter(largeShareholderSearchFilter));

    // Reset paginator UI to first page
    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    // Reload data
    this.loadLargeShareholders();
  }

  onExportToCsv(): void {
    this.spinnerService.show();

    this.largeShareholderService.exportLargeShareholdersToCsv({
      ...this.currentSearchFilter,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `large_shareholders_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.toast.success('Large shareholders exported successfully');
      },
      error: (error) => {
        console.error('Error exporting large shareholders:', error);
        this.toast.error('Error exporting large shareholders');
        this.spinnerService.hide();
      }
    });
  }

  onReadyToMatch(event: {rightItem?: LargeShareholderDto, matchedItems?: LargeShareholderDto[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onDeleteLargeShareholder(largeShareholder: LargeShareholderDto): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(largeShareholder.name)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalLargeShareholders = this.largeShareholders;
          const originalTotalItems = this.totalItems;

          // 1. Optimistically remove from local state immediately
          this.largeShareholders = this.largeShareholders.filter(ls => ls.id !== largeShareholder.id);
          this.totalItems = this.totalItems - 1;

          // 2. Call backend API
          this.spinnerService.show();
          this.largeShareholderService.deleteLargeShareholder(largeShareholder.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Large shareholder deleted successfully');
              // 3. On success: Reload the list to ensure consistency with backend
              this.loadLargeShareholders();
            },
            error: (error) => {
              console.error('Error deleting large shareholder:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.largeShareholders = originalLargeShareholders;
              this.totalItems = originalTotalItems;
              this.toast.error(`<strong>Failed to delete Large Shareholder:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onLargeShareholderCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select large shareholders to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original lists for rollback in case of error
        const originalLargeShareholders = this.largeShareholders;
        const originalTotalItems = this.totalItems;

        // 1. Optimistically remove merged items from local state
        this.largeShareholders = this.largeShareholders.filter(ls => !bulkMergeRequest.leftIds.includes(ls.id));
        this.totalItems = this.totalItems - bulkMergeRequest.leftIds.length;

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.largeShareholderService.bulkMergeLargeShareholders(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Large shareholders merged successfully');
            // 3. On success: Clear the generic list's internal matching state and reload the list
            this.genericList?.clearMatchingState();
            this.loadLargeShareholders();
          },
          error: (error) => {
            console.error('Error merging large shareholders:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original lists
            this.largeShareholders = originalLargeShareholders;
            this.totalItems = originalTotalItems;
            // Restore selection state for retry
            this.rightItem = originalLargeShareholders.find(ls => ls.id === bulkMergeRequest.rightId);
            this.matchedItems = originalLargeShareholders.filter(ls => bulkMergeRequest.leftIds.includes(ls.id));
            this.toast.error(`<strong>Failed to merge Large Shareholders:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    // Component cleanup
  }
}
